import type { HistoricalTimelineDecade } from "../generated-types";
import { eventMatchesCategoryKeys } from "./timeline-utils";

export type DecadeMetricSeries = {
	values: number[];
};

export const DECADE_HOVER_GRAPH_VIEWBOX_WIDTH = 100;
export const DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT = 40;

const VIEWBOX_WIDTH = DECADE_HOVER_GRAPH_VIEWBOX_WIDTH;
const VIEWBOX_HEIGHT = DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT;
const CHART_PADDING_TOP = 4;
const CHART_PADDING_BOTTOM = 10;

export function parseMetricValue(raw?: string): number | null {
	if (!raw?.trim()) {
		return null;
	}

	const normalized = raw
		.trim()
		.replace(/,/g, "")
		.replace(/[^\d.-]/g, "");
	if (!normalized) {
		return null;
	}

	const parsed = Number.parseFloat(normalized);
	return Number.isFinite(parsed) ? parsed : null;
}

export function getDecadeMetricSeries(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[],
	metric: "pricePerBarrel" | "population"
): DecadeMetricSeries {
	const values: number[] = [];

	for (const year of decade.years ?? []) {
		const events = (year.events ?? []).filter((event) =>
			eventMatchesCategoryKeys(event, activeCategoryKeys)
		);

		for (const event of events) {
			const value = parseMetricValue(event[metric]);
			if (value !== null) {
				values.push(value);
				break;
			}
		}
	}

	if (values.length === 0) {
		const decadeValue = parseMetricValue(decade[metric]);
		if (decadeValue !== null) {
			values.push(decadeValue, decadeValue);
		}
	}

	return { values };
}

export function getDecadeMetricAverage(series: DecadeMetricSeries): number | null {
	if (series.values.length === 0) {
		return null;
	}

	const total = series.values.reduce((sum, value) => sum + value, 0);
	return total / series.values.length;
}

export function formatMetricAverage(value: number): string {
	const rounded = Math.round(value * 10) / 10;
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

type SparklineLayout = {
	linePath: string;
	areaPath: string;
};

function buildSparklineLayout(values: number[]): SparklineLayout | null {
	if (values.length === 0) {
		return null;
	}

	const width = VIEWBOX_WIDTH;
	const height = VIEWBOX_HEIGHT;
	const baselineY = height - CHART_PADDING_BOTTOM;
	const chartTop = CHART_PADDING_TOP;
	const chartHeight = baselineY - chartTop;

	if (values.length === 1) {
		const y = chartTop + chartHeight / 2;
		const linePath = `M 0 ${y} L ${width} ${y}`;
		const areaPath = `${linePath} L ${width} ${baselineY} L 0 ${baselineY} Z`;

		return { linePath, areaPath };
	}

	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min || 1;

	const layoutPoints = values.map((value, index) => {
		const x = (index / (values.length - 1)) * width;
		const y = chartTop + chartHeight - ((value - min) / range) * chartHeight;
		return { x, y };
	});

	const linePath = layoutPoints
		.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
		.join(" ");
	const areaPath = `${linePath} L ${layoutPoints.at(-1)!.x} ${baselineY} L ${layoutPoints[0].x} ${baselineY} Z`;

	return { linePath, areaPath };
}

function getSparklineMarkup(
	series: DecadeMetricSeries,
	idPrefix: string,
	modifier: "price" | "population"
): string {
	const layout = buildSparklineLayout(series.values);
	if (!layout) {
		return "";
	}

	const gradientId = `${idPrefix}-${modifier}-gradient`;
	const clipId = `${idPrefix}-${modifier}-clip`;

	return `
		<div class="historical-timeline__decade-hover-graph historical-timeline__decade-hover-graph--${modifier}">
			<svg
				class="historical-timeline__decade-hover-graph-svg"
				viewBox="0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}"
				preserveAspectRatio="none"
				aria-hidden="true"
				focusable="false"
			>
				<defs>
					<linearGradient id="${gradientId}" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stop-color="currentColor" stop-opacity="0.38" />
						<stop offset="100%" stop-color="currentColor" stop-opacity="0" />
					</linearGradient>
					<clipPath id="${clipId}">
						<rect
							class="historical-timeline__decade-hover-graph-clip"
							x="0"
							y="0"
							width="0"
							height="${VIEWBOX_HEIGHT}"
						/>
					</clipPath>
				</defs>
				<g clip-path="url(#${clipId})">
					<g class="historical-timeline__decade-hover-graph-series">
						<path class="historical-timeline__decade-hover-graph-area" d="${layout.areaPath}" fill="url(#${gradientId})" />
						<path class="historical-timeline__decade-hover-graph-line" d="${layout.linePath}" />
					</g>
				</g>
			</svg>
		</div>
	`.trim();
}

export function getDecadeHoverGraphsMarkup(
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[],
	idPrefix: string
): string {
	const priceSeries = getDecadeMetricSeries(decade, activeCategoryKeys, "pricePerBarrel");
	const populationSeries = getDecadeMetricSeries(decade, activeCategoryKeys, "population");

	const priceMarkup = getSparklineMarkup(priceSeries, idPrefix, "price");
	const populationMarkup = getSparklineMarkup(populationSeries, idPrefix, "population");

	if (!priceMarkup && !populationMarkup) {
		return "";
	}

	return `
		<div class="historical-timeline__decade-hover-graphs">
			${priceMarkup}
			${populationMarkup}
		</div>
	`.trim();
}
